import { css, styled } from 'styled-components';
import { ReactNode } from 'react';
import { useHref } from 'react-router-dom';

import { ForaLogo } from '../../../assets/icons';
import { Flex } from '../../Layout';
import { LogoWithEServiceText } from '../common/LogoWithEServiceText';
import { topBarStyle } from '../common/commonStyles';
import { EServiceTopBar } from '../EServiceTopBar';
import { zIndex } from '../../../styles/zIndex';
import { colors } from '../../../styles/colors';
import { SkipLink } from '../../Link/BaseLink';

const StyledNav = styled.nav<{ $sticky?: boolean }>`
  ${topBarStyle}

  ${({ $sticky }) =>
    $sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: ${zIndex.modal};
    `}
`;

type NavbarProps = {
  /** Text displayed next to logo, such as "/ E-tjänst" */
  eServiceText?: string;
  /** Skip to main content link https://www.w3schools.com/accessibility/accessibility_skip_links.php */
  skipLink: {
    elementId: string;
    skipToContentText: string;
  };
  /** Stickt to top */
  sticky?: boolean;
  /** Items to display in bar */
  children: ReactNode;
};

export const Navbar = ({ eServiceText, skipLink, sticky, children }: NavbarProps) => {
  const skipLinkHref = useHref(`#${skipLink.elementId}`);

  if (!children) {
    // Use top bar if it does not have any navigation elements
    return <EServiceTopBar eServiceText={eServiceText} />;
  }

  return (
    <>
      <SkipToContentNav aria-label="Snabbmeny">
        <SkipLink href={skipLinkHref} skipToContentText={skipLink.skipToContentText} />
      </SkipToContentNav>
      <StyledNav $sticky={sticky} aria-label="Huvudmeny">
        <StyledFlex alignItems="flex-end" justifyContent="flex-end" height="100%" columnGap={30}>
          <Flex alignItems="center" height="100%">
            {eServiceText ? <LogoWithEServiceText eServiceText={eServiceText} /> : <ForaLogo width={100} />}
          </Flex>
          {children}
        </StyledFlex>
      </StyledNav>
    </>
  );
};

const StyledFlex = styled(Flex)`
  // Push logo to the left
  > :first-child {
    margin-right: auto;
  }
`;

const SkipToContentNav = styled.nav`
  background-color: ${colors.foraBlue};
`;
